<template>
  <div>
    <content-container>
      <div class="digital-service">
        <transition name="fade" mode="out-in">
          <loader-with-message v-if="dataLoading"
                               :key="'loadernba'"
                               :has-error="dataFetchError"
                               @retry="fetchNbaPackages"/>
          <div v-else :key="'packagesnba'">
            <transition name="fade" mode="out-in">
              <account-status-blocked v-if="userStatus === 'BLOCKED'" :key="'status-blocked'" :resources-data="$props.resourcesData"/>
              <account-status-on-hold v-else-if="userStatus === 'ON_HOLD'" :key="'status-on-hold'" :resources-data="$props.resourcesData"/>
              <div v-else :key="'status-active'">
                <base-heading>{{ $t('digitalServices.nbaDigitalServiceTitle') }}</base-heading>
                <h4 class="subtitle">{{ $t('digitalServices.nba.title') }}</h4>
                <div class="description">{{ $t('digitalServices.nba.description') }}</div>
                <div class="cards-container">
                  <digital-service-card v-for="(card, idx) in nbaAvailablePackages"
                                        :key="'package'+idx"
                                        :title="card.name"
                                        :description="card.description"
                                        :logo="card.image.url"
                                        :tooltip="card.tooltipDescription"
                                        :price="card.actualPrice.formattedValue"
                                        :activation-disabled="card.activationLimitReached"
                                        :free-status="card.actualPrice.value <= 0"
                                        :old-price="card.originalPrice.formattedValue"
                                        @activate-service="activateService(card.externalId)"
                                        @deactivate-service="deactivateService"/>
                </div>
              </div>
            </transition>
            <purchase-history-table v-if="nbaActivatedPackages.length" :activated-packages="nbaActivatedPackages"/>
          </div>
        </transition>
      </div>
    </content-container>
    <div class="digital-service">
      <confirmation-modal :visible="nbaActivationModal"
                          :closeButtonVisible="false"
                          @close="nbaActivationModal = false"
                          :cancel-button="true"
                          confirm-type="empty"
                          :confirm-title="$t('digitalServices.nba.confirmationNbaModal.title')"
                          :confirm-description="$t('digitalServices.nba.confirmationNbaModal.description')"
                          :confirm-text="$t('common.activate')"
                          @confirm="activateNbaApi"
                          :loading="confirmationLoader"
                          :button-disabled="buttonDisabled"/>
      <confirmation-modal :visible="nbaSuccessActivation"
                          @close="resetData"
                          confirm-type="empty"
                          :confirm-title="$t('digitalServices.nba.nbaSuccessActivationModal.title')"
                          :confirm-description="$t('digitalServices.nba.nbaSuccessActivationModal.description')"
                          :confirm-button="false"
                          :cancel-button="false">
        <div slot="extra-section">
          <p class="code-label">{{ $t('digitalServices.nba.nbaSuccessActivationModal.codeLabel') }}</p>
          <v-popover class="digital-tooltip" popover-class="digital-service-popover" trigger="hover" :auto-hide="false" placement="top">
            <button class="code-btn" @click.stop.prevent="copyCodeToClipboard">
              <span class="code">{{ code }}</span>
            </button>
            <template slot="popover">
              <div class="digital-tooltip-description">
                <div class="tooltip-content-wrapper">
                  <transition name="fade" mode="out-in">
                    <img v-if="codeCopied" src="/assets/images/green_circle_checkmark.svg" alt="" class="digital-success-icon"/>
                  </transition>
                  {{ codeCopied ? $t('digitalServices.nba.nbaSuccessActivationModal.tooltipSuccessCodeCopy') : $t('digitalServices.nba.nbaSuccessActivationModal.tooltipCopyCodeText') }}
                </div>
              </div>
            </template>
          </v-popover>
          <div class="app-links">
            <a :href="$t(`digitalServices.nba.appLinks.appStore`)" target="_blank" rel="nofollow">
              <img class="store-button" src="/assets/images/app-store-badge.png" alt="app-store">
            </a>
            <a :href="$t(`digitalServices.nba.appLinks.googlePlay`)" target="_blank" rel="nofollow">
              <img class="store-button" src="/assets/images/google-play-badge.png" alt="google-play">
            </a>
            <a href="https://watch.nba.com/packages" target="_blank" rel="nofollow">
              <img class="store-button" src="/assets/images/check-on-web.png" alt="check-on-web">
            </a>
          </div>
        </div>
      </confirmation-modal>
    </div>
    <digital-instruction-sticky-button service-name="NBA" />
  </div>
</template>

<script>

import DigitalServiceCard from '@/components/digital-services/digital-service-card'
import AccountStatusBlocked from '@/components/services/account-status/account-status-blocked'
import selectionBlockEvents from '@/mixins/selectionBlockEvents'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import LoaderWithMessage from '@/components/common/loader-with-message'
import AccountStatusOnHold from '@/components/services/account-status/account-status-on-hold'
import PurchaseHistoryTable from '@/components/common/purchase-history/purchase-history-table'
import ContentContainer from '@/components/common/main-layout/content-container'
import DigitalInstructionStickyButton from '@/components/digital-services/digital-instruction-sticky-button'

export default {
  name: 'digital-service-nba',
  components: { DigitalInstructionStickyButton, ContentContainer, PurchaseHistoryTable, AccountStatusOnHold, LoaderWithMessage, AccountStatusBlocked, DigitalServiceCard, ConfirmationModal },
  props: {
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  mixins: [selectionBlockEvents, errorMessageHandler],
  data () {
    return {
      dataLoading: true,
      dataFetchError: false,
      buttonDisabled: false,
      nbaAvailablePackages: [],
      nbaActivatedPackages: [],
      userNotEligible: false,
      userStatus: '',
      nbaActivationModal: false,
      nbaSuccessActivation: false,
      externalId: '',
      code: '',
      codeCopied: false,
      confirmationLoader: false
    }
  },
  methods: {
    fetchNbaPackages () {
      if (this.$props.resourcesData.subscriptionInfo && this.$props.resourcesData.subscriptionInfo.customerId) {
        this.dataLoading = true
        this.dataFetchError = false
        this.blockSidebarSelection()
        const customerId = this.$props.resourcesData.subscriptionInfo.customerId
        this.$apiRequest.get(`user/nba/packages?customerId=${customerId}`)
          .then(response => {
            this.nbaAvailablePackages = response.data.available || []
            this.nbaActivatedPackages = response.data.activated || []
            this.userStatus = response.data.contractStatus
            this.dataLoading = false
          })
          .catch(e => {
            this.dataFetchError = true
            // this.handleErrorMessage(e.data.response)
          })
          .finally(() => {
            this.unblockSidebarSelection()
          })
      }
    },
    activateService (externalId) {
      this.nbaActivationModal = true
      this.externalId = externalId
    },
    activateNbaApi () {
      this.buttonDisabled = true
      this.confirmationLoader = true
      const id = this.$props.resourcesData.subscriptionInfo.customerId
      const dataToSend = {
        customerId: id,
        externalId: this.externalId
      }
      this.$apiRequest.post('user/nba/activate', dataToSend)
        .then(response => {
          if (response.status === 200) {
            this.nbaActivationModal = false
            this.nbaSuccessActivation = true
            this.code = response.data.activationCode
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.buttonDisabled = false
          this.confirmationLoader = false
        })
    },
    copyCodeToClipboard () {
      const str = document.getElementsByClassName('code')[0].innerText
      const el = document.createElement('textarea')
      el.value = str
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      this.codeCopied = true
      document.body.removeChild(el)
    },
    deactivateService () {
      console.log('deactivate service')
    },
    deactivateNbaApi () {
      this.buttonDisabled = true
      this.confirmationLoader = true
      const id = this.$props.resourcesData.subscriptionInfo.customerId
      const dataToSend = {
        customerId: id,
        externalId: this.externalId
      }
      this.$apiRequest.post('user/nba/activate', dataToSend)
        .then(response => {
          if (response.status === 200) {
            this.nbaActivationModal = false
            this.nbaSuccessActivation = true
            this.code = response.data.activationCode
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.buttonDisabled = false
          this.confirmationLoader = false
        })
    },
    resetData () {
      this.nbaSuccessActivation = false
      this.codeCopied = false
      this.fetchNbaPackages()
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.fetchNbaPackages()
    })
  }
}
</script>

<style scoped lang="scss" src="./styles/digital-service-page.scss"></style>
